import type { HTMLAttributes } from "react";
import { useRef, useState } from "react";

import { useTranslate } from "../../../utils/translate";
import { sizes } from "../../design-tokens/dimensions";
import { Button } from "../Button";
import { ButtonContainer, Content, Root, Wrapper } from "./styles";
import { translations } from "./translation";

export type ShowMoreProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Height of collapsed state
   */
  initialHeight?: number;
  /**
   * Should load expanded (default is collapsed)
   */
  expandOnLoad?: boolean;
  /**
   * Button text displayed for expanded/collapsed states
   */
  buttonContent?: {
    whenExpanded: string;
    whenCollapsed: string;
  };
};

export function ShowMore({
  buttonContent,
  expandOnLoad = false,
  initialHeight = sizes.s128.raw,
  children,
  ...rest
}: ShowMoreProps): JSX.Element {
  const t = useTranslate(translations);

  const [isExpanded, setIsExpanded] = useState(expandOnLoad);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    expandOnLoad ? undefined : initialHeight
  );

  const ref = useRef<HTMLDivElement>(null);

  const clickHandler = () => {
    setIsExpanded((prev) => !prev);

    if (!ref.current) {
      return;
    }
    if (isExpanded) {
      setContentHeight(initialHeight);
    } else {
      setContentHeight(ref.current.scrollHeight);
    }
  };

  const whenCollapsedText = buttonContent?.whenCollapsed ?? t("showMore.whenCollapsed");
  const whenExpandedText = buttonContent?.whenExpanded ?? t("showMore.whenExpanded");

  return (
    <Root data-testid={ShowMore.name}>
      <Wrapper isExpanded={isExpanded} maxHeight={contentHeight} {...rest}>
        <Content
          // Waits for all images to load
          // This is so that if the component is expanded by default, we get the correct initial height
          onLoad={() => (expandOnLoad ? setContentHeight(ref.current?.scrollHeight) : null)}
          ref={ref}
        >
          {children}
        </Content>
      </Wrapper>
      <ButtonContainer>
        <Button variant="inline" onClick={clickHandler}>
          {isExpanded ? whenExpandedText : whenCollapsedText}
        </Button>
      </ButtonContainer>
    </Root>
  );
}
